import type { Permission } from './actions';
import { PERMISSIONS } from './permissions';

export const SYSTEM_ROLES = {
  platformAdmin: 'platform-admin',
  orgAdmin: 'org-admin',
  pmo: 'pmo',
  deliveryManager: 'delivery-manager',
  projectManager: 'project-manager',
  teamMember: 'team-member',
  hrAdmin: 'hr-admin',
  hrSpecialist: 'hr-specialist',
  viewer: 'viewer',
  maintainer: 'maintainer',
  trainingAdmin: 'training-admin',
  trainingAuthor: 'training-author',
} as const;

export type SystemRole = (typeof SYSTEM_ROLES)[keyof typeof SYSTEM_ROLES];

const SYSTEM_ROLE_VALUES = new Set<string>(Object.values(SYSTEM_ROLES));

export const isSystemRole = (value: string): value is SystemRole => SYSTEM_ROLE_VALUES.has(value);

/**
 * Highest to lowest, for the one place order matters: picking which held role
 * becomes the active one, at sign-in and whenever a stale session refreshes
 * into a role the person no longer holds. A platform administrator who also
 * delivers defaults to seeing the app as a platform administrator, not
 * whichever role a database query happened to return first.
 */
export const ROLE_PRIORITY: readonly SystemRole[] = [
  SYSTEM_ROLES.platformAdmin,
  SYSTEM_ROLES.orgAdmin,
  SYSTEM_ROLES.pmo,
  SYSTEM_ROLES.deliveryManager,
  SYSTEM_ROLES.projectManager,
  SYSTEM_ROLES.hrAdmin,
  SYSTEM_ROLES.hrSpecialist,
  SYSTEM_ROLES.teamMember,
  // Below team-member on purpose: the training roles are granted beside a delivery or HR
  // role, and ranking them higher would change which role such a person lands on by default.
  SYSTEM_ROLES.trainingAdmin,
  SYSTEM_ROLES.trainingAuthor,
  // Above viewer on purpose: a person can hold viewer beside other roles, and the
  // server authorizes with one active role at a time, so a maintainer ranked below it
  // would land on viewer and be refused by every sync console page until they switched.
  SYSTEM_ROLES.maintainer,
  SYSTEM_ROLES.viewer,
];

/**
 * The first role in priority order that the person actually holds, or `null`
 * when they hold none of the twelve — the state of everyone on their first
 * sign-in, since nothing here manufactures access nobody has been given.
 */
export const pickDefaultRole = (heldRoles: readonly SystemRole[]): SystemRole | null =>
  ROLE_PRIORITY.find((role) => heldRoles.includes(role)) ?? null;

/**
 * Held by `training-admin` and, beside everything else, by `org-admin`. No
 * `group:read`: the department picker reads through training-service and is gated on
 * `training-group:read`.
 */
const TRAINING_ADMIN_PERMISSIONS: readonly Permission[] = [
  PERMISSIONS.questionnaireManage,
  PERMISSIONS.trainingGroupManage,
  PERMISSIONS.trainingAssignmentManage,
  PERMISSIONS.trainingAttemptApprove,
  PERMISSIONS.certificateManage,
  PERMISSIONS.trainingSettingsManage,
];

/**
 * Seed matrix. A role's live permission set lives in Postgres (user-service owns
 * it) — this is the shape the seeder writes and the fallback the gateway uses
 * before the first sync.
 */
export const SYSTEM_ROLE_PERMISSIONS: Record<SystemRole, readonly Permission[]> = {
  // Administration only, by decision: who exists, what they may do, how they
  // are grouped, which applications are registered, and the directory sync.
  // An administrator who also delivers holds a delivery role beside this one
  // and switches between them. Holding every permission made the role a
  // super user, which the product owner ruled out.
  [SYSTEM_ROLES.platformAdmin]: [
    PERMISSIONS.userManage,
    PERMISSIONS.roleManage,
    PERMISSIONS.groupManage,
    PERMISSIONS.applicationManage,
    PERMISSIONS.directoryManage,
  ],

  [SYSTEM_ROLES.orgAdmin]: [
    PERMISSIONS.userManage,
    PERMISSIONS.roleManage,
    PERMISSIONS.groupManage,
    PERMISSIONS.applicationManage,
    PERMISSIONS.clientManage,
    PERMISSIONS.sowManage,
    PERMISSIONS.projectManage,
    PERMISSIONS.allocationManage,
    PERMISSIONS.resourceRoleManage,
    PERMISSIONS.documentManage,
    PERMISSIONS.milestoneManage,
    PERMISSIONS.taskManage,
    PERMISSIONS.candidateManage,
    PERMISSIONS.candidateDocumentManage,
    PERMISSIONS.employeeManage,
    PERMISSIONS.workflowManage,
    PERMISSIONS.formTemplateManage,
    PERMISSIONS.emailTemplateManage,
    PERMISSIONS.directoryRead,
    PERMISSIONS.directNdaRead,
    PERMISSIONS.directNdaCreate,
    PERMISSIONS.directNdaUpdate,
    PERMISSIONS.directNdaSettingsUpdate,
    ...TRAINING_ADMIN_PERMISSIONS,
  ],

  [SYSTEM_ROLES.pmo]: [
    PERMISSIONS.userRead,
    PERMISSIONS.groupRead,
    PERMISSIONS.clientManage,
    PERMISSIONS.sowManage,
    PERMISSIONS.projectManage,
    PERMISSIONS.projectApprove,
    PERMISSIONS.allocationManage,
    PERMISSIONS.resourceRoleManage,
    PERMISSIONS.documentManage,
    PERMISSIONS.milestoneManage,
    PERMISSIONS.taskManage,
  ],

  [SYSTEM_ROLES.deliveryManager]: [
    PERMISSIONS.userRead,
    PERMISSIONS.groupRead,
    PERMISSIONS.clientRead,
    PERMISSIONS.sowManage,
    PERMISSIONS.projectManage,
    PERMISSIONS.projectApprove,
    PERMISSIONS.allocationManage,
    PERMISSIONS.resourceRoleRead,
    PERMISSIONS.documentManage,
    PERMISSIONS.milestoneManage,
    PERMISSIONS.taskManage,
  ],

  [SYSTEM_ROLES.projectManager]: [
    PERMISSIONS.userRead,
    PERMISSIONS.groupRead,
    PERMISSIONS.clientRead,
    PERMISSIONS.sowRead,
    PERMISSIONS.sowCreate,
    PERMISSIONS.sowUpdate,
    PERMISSIONS.projectCreate,
    PERMISSIONS.projectRead,
    PERMISSIONS.projectUpdate,
    PERMISSIONS.allocationRead,
    PERMISSIONS.resourceRoleRead,
    PERMISSIONS.allocationCreate,
    PERMISSIONS.allocationUpdate,
    PERMISSIONS.documentRead,
    PERMISSIONS.documentCreate,
    PERMISSIONS.milestoneManage,
    PERMISSIONS.taskManage,
  ],

  [SYSTEM_ROLES.teamMember]: [
    PERMISSIONS.userRead,
    PERMISSIONS.sowRead,
    PERMISSIONS.projectRead,
    PERMISSIONS.allocationRead,
    PERMISSIONS.resourceRoleRead,
    PERMISSIONS.documentRead,
    PERMISSIONS.milestoneRead,
    PERMISSIONS.taskRead,
    PERMISSIONS.taskCreate,
    PERMISSIONS.taskUpdate,
  ],

  [SYSTEM_ROLES.hrAdmin]: [
    PERMISSIONS.userManage,
    PERMISSIONS.groupRead,
    PERMISSIONS.candidateManage,
    PERMISSIONS.candidateAssign,
    // Only HR admins may reveal a social security number or date of birth to begin with.
    PERMISSIONS.candidateSensitiveRead,
    PERMISSIONS.candidateDocumentManage,
    PERMISSIONS.employeeManage,
    PERMISSIONS.workflowManage,
    PERMISSIONS.formTemplateManage,
    PERMISSIONS.emailTemplateManage,
    PERMISSIONS.directoryRead,
    PERMISSIONS.directNdaRead,
    PERMISSIONS.directNdaCreate,
    PERMISSIONS.directNdaUpdate,
    PERMISSIONS.directNdaSettingsUpdate,
  ],

  [SYSTEM_ROLES.hrSpecialist]: [
    PERMISSIONS.userRead,
    // Departments are groups; placing a candidate needs the list.
    PERMISSIONS.groupRead,
    PERMISSIONS.candidateRead,
    PERMISSIONS.candidateCreate,
    PERMISSIONS.candidateUpdate,
    PERMISSIONS.candidateApprove,
    PERMISSIONS.candidateDocumentManage,
    PERMISSIONS.employeeRead,
    PERMISSIONS.workflowRead,
    PERMISSIONS.formTemplateRead,
    PERMISSIONS.emailTemplateRead,
    PERMISSIONS.directNdaRead,
    PERMISSIONS.directNdaCreate,
    PERMISSIONS.directNdaUpdate,
  ],

  // Reads the sync logs and nothing else. Granted by an administrator; the directory group
  // below is only the name an Entra group would carry. Of the roles, only platform-admin is
  // ever granted from an Entra group (the admin group, read through Graph), never this one.
  [SYSTEM_ROLES.maintainer]: [PERMISSIONS.directoryRead],

  [SYSTEM_ROLES.trainingAdmin]: [PERMISSIONS.userRead, ...TRAINING_ADMIN_PERMISSIONS],

  [SYSTEM_ROLES.trainingAuthor]: [
    PERMISSIONS.userRead,
    PERMISSIONS.questionnaireRead,
    PERMISSIONS.questionnaireCreate,
    PERMISSIONS.questionnaireUpdate,
    PERMISSIONS.trainingGroupManage,
    PERMISSIONS.trainingAssignmentRead,
    PERMISSIONS.trainingAssignmentCreate,
    PERMISSIONS.trainingAssignmentUpdate,
    PERMISSIONS.trainingAttemptApprove,
    PERMISSIONS.certificateRead,
    PERMISSIONS.trainingSettingsRead,
  ],

  [SYSTEM_ROLES.viewer]: [
    PERMISSIONS.projectRead,
    PERMISSIONS.allocationRead,
    PERMISSIONS.resourceRoleRead,
    PERMISSIONS.documentRead,
    PERMISSIONS.milestoneRead,
    PERMISSIONS.taskRead,
  ],
};

/**
 * Entra ID group names that map onto system roles. `ad-sync-service` provisions
 * one security group per entry and the directory remains the outward projection
 * of these roles — Postgres stays the source of truth. Nothing reads these groups
 * back: the one inbound group is the platform-admin group named by
 * ENTRA_ADMIN_GROUP_ID in employee-service, not an entry here.
 */
export const ROLE_DIRECTORY_GROUPS: Record<SystemRole, string> = {
  [SYSTEM_ROLES.platformAdmin]: 'cyrino-platform-admin',
  [SYSTEM_ROLES.orgAdmin]: 'cyrino-org-admin',
  [SYSTEM_ROLES.pmo]: 'cyrino-pmo',
  [SYSTEM_ROLES.deliveryManager]: 'cyrino-delivery-manager',
  [SYSTEM_ROLES.projectManager]: 'cyrino-project-manager',
  [SYSTEM_ROLES.teamMember]: 'cyrino-team-member',
  [SYSTEM_ROLES.hrAdmin]: 'cyrino-hr-admin',
  [SYSTEM_ROLES.hrSpecialist]: 'cyrino-hr-specialist',
  [SYSTEM_ROLES.viewer]: 'cyrino-viewer',
  [SYSTEM_ROLES.maintainer]: 'cyrino-maintainer',
  [SYSTEM_ROLES.trainingAdmin]: 'cyrino-training-admin',
  [SYSTEM_ROLES.trainingAuthor]: 'cyrino-training-author',
};

/**
 * Display names for the system roles. Not derived by title-casing because that
 * turns the acronyms in "pmo" and "hr-admin" into "Pmo" and "Hr Admin".
 */
export const SYSTEM_ROLE_LABELS: Record<SystemRole, string> = {
  [SYSTEM_ROLES.platformAdmin]: 'Platform Admin',
  [SYSTEM_ROLES.orgAdmin]: 'Org Admin',
  [SYSTEM_ROLES.pmo]: 'PMO',
  [SYSTEM_ROLES.deliveryManager]: 'Delivery Manager',
  [SYSTEM_ROLES.projectManager]: 'Project Manager',
  [SYSTEM_ROLES.teamMember]: 'Team Member',
  [SYSTEM_ROLES.hrAdmin]: 'HR Admin',
  [SYSTEM_ROLES.hrSpecialist]: 'HR Specialist',
  [SYSTEM_ROLES.maintainer]: 'Maintainer',
  [SYSTEM_ROLES.trainingAdmin]: 'Training Admin',
  [SYSTEM_ROLES.trainingAuthor]: 'Training Author',
  [SYSTEM_ROLES.viewer]: 'Viewer',
};

/**
 * One plain-English line per system role, written from its permissions above, for the
 * screens that ask an administrator to pick a role. Keep it in step with the matrix.
 */
export const SYSTEM_ROLE_SUMMARIES: Record<SystemRole, string> = {
  [SYSTEM_ROLES.platformAdmin]:
    'Looks after who exists and what they may do: people, roles, departments, applications and the directory sync.',
  [SYSTEM_ROLES.orgAdmin]:
    'The older all-round administrator: access plus delivery, hiring and training. Kept for current holders only.',
  [SYSTEM_ROLES.pmo]:
    'Runs delivery: clients, statements of work, projects and allocation, and approves projects.',
  [SYSTEM_ROLES.deliveryManager]:
    'Runs projects and statements of work, allocates people and approves projects.',
  [SYSTEM_ROLES.projectManager]:
    'Creates and updates projects, statements of work and allocations, and manages milestones and tasks.',
  [SYSTEM_ROLES.teamMember]: 'Sees projects and allocations and works on tasks.',
  [SYSTEM_ROLES.hrAdmin]:
    'Runs hiring end to end: candidates, workflows, templates and employee records, including sensitive answers. Also manages people.',
  [SYSTEM_ROLES.hrSpecialist]:
    'Moves candidates through hiring: creates, updates and approves them, and reads employee records and templates.',
  [SYSTEM_ROLES.maintainer]:
    'Reads the Zoho sync runs and the events each one wrote, nothing else.',
  [SYSTEM_ROLES.trainingAdmin]:
    'Runs training: questionnaires, groups, assignments, approvals, certificates and settings.',
  [SYSTEM_ROLES.trainingAuthor]: 'Writes questionnaires, assigns them and marks written answers.',
  [SYSTEM_ROLES.viewer]:
    'Read-only view of projects, allocations, documents, milestones and tasks.',
};

/** Empty for a role the catalogue does not know, such as a custom one. */
export const systemRoleSummary = (roleName: string): string =>
  isSystemRole(roleName) ? SYSTEM_ROLE_SUMMARIES[roleName] : '';

/** Falls back to a title-cased guess for a role the map doesn't know about yet. */
export const systemRoleLabel = (roleName: string): string =>
  isSystemRole(roleName)
    ? SYSTEM_ROLE_LABELS[roleName]
    : roleName
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

/**
 * A role counts as "administrator" if it grants `role:manage` — the one
 * permission that lets someone grant or revoke roles at all. Removing a
 * person's last one from their own account would strand them with no way to
 * get it back except another administrator's help.
 */
export const adminRoleNamesOf = (
  roles: ReadonlyArray<{ name: string; permissions: readonly string[] }>,
): Set<string> =>
  new Set(
    roles
      .filter((role) => role.permissions.includes(PERMISSIONS.roleManage))
      .map((role) => role.name),
  );
